import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import SecurityFeatures from "@/components/ui/SecurityFeatures";
import {
  ArrowRight,
  Lock,
  Shield,
  Database,
  Eye,
  Server,
  Globe,
  Check,
} from "lucide-react";
import FAQ from "@/components/ui/FAQ";
import SecureInfrastructure from "@/components/ui/SecureInfrastructure";

const faqData = [
  {
    question: "How does Bridge Format AIQ protect my data?",
    answer:
      "Bridge Format AIQ protects your data through a multi-layered security approach, including encryption, access controls, and secure infrastructure. Data is encrypted both in transit and at rest, and access is restricted using role-based permissions.",
  },
  {
    question: "Is my data used to train AI models?",
    answer:
      "No — your data is not used to train AI models without your explicit consent. We ensure strict separation between customer data and any model training processes to maintain privacy and confidentiality.",
  },
  {
    question: "How is data encrypted on the platform?",
    answer:
      "We use industry-standard encryption practices including TLS 1.2+ for data in transit and AES-256 encryption for data at rest. This ensures your data is protected during transfer and storage.",
  },
  {
    question: "Who can access my data?",
    answer:
      "Only authorized users within your organization can access your data. We enforce role-based access control (RBAC), least-privilege access, and secure authentication mechanisms. Internal team access is strictly limited and audited.",
  },
  {
    question: "Do you support multi-factor authentication (MFA)?",
    answer:
      "Yes, Bridge Format AIQ supports multi-factor authentication (MFA) to add an extra layer of security for user accounts.",
  },
  {
    question: "Where is my data stored?",
    answer:
      "Your data is stored in secure cloud environments with strict access controls. We also offer data residency options depending on your business and regulatory needs.",
  },
  {
    question: "Can I control how long my data is stored?",
    answer:
      "Yes, you can configure data retention policies based on your requirements. You can also request data deletion at any time.",
  },
  {
    question: "How do you ensure AI processing is secure?",
    answer:
      "We implement strict safeguards around AI processing including secure data pipelines, controlled input/output handling, optional data masking and anonymization, and no unauthorized reuse of customer data.",
  },
];

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const securityFeatures = [
  {
    icon: Lock,
    title: "End-to-End Encryption",
    description:
      "All data is encrypted in transit (TLS 1.3) and at rest (AES-256). Zero-knowledge architecture where applicable.",
  },
  {
    icon: Shield,
    title: "Role-Based Access Control",
    description:
      "Granular permissions with department-level, role-level, and document-level access controls.",
  },
  {
    icon: Eye,
    title: "Full Audit Logs",
    description:
      "Complete audit trail of all document processing, access, and administrative actions with tamper-proof logging.",
  },
  {
    icon: Database,
    title: "Secure Data Isolation",
    description:
      "Multi-tenant architecture with strict data isolation. Each organization data is logically separated.",
  },
  {
    icon: Server,
    title: "Compliance-Aligned Architecture",
    description:
      "Built to support SOC 2 Type II, ISO 27001, GDPR, HIPAA, and other regulatory frameworks.",
  },
  {
    icon: Globe,
    title: "Data Residency Options",
    description:
      "Choose where your data is processed and stored. Regional deployment options available.",
  },
];

const deploymentOptions = [
  {
    name: "Encryption ",
    description: "",
    features: [
      "Data encrypted in transit (TLS 1.2+) ",
      "Data encrypted at rest (AES-256) ",
    ],
  },
  {
    name: "Data Isolation  ",
    description: "",
    features: [
      " Logical separation between customer environments",
      "Strict tenant-level access boundaries",
    ],
  },
  {
    name: "Data Retention & Deletion",
    description: "",
    features: [
      "Configurable data retention policies ",
      "Secure deletion on request or expiry ",
    ],
  },
];

export const Security = () => {
  return (
    <div className="pt-16 md:pt-20" data-testid="security-page">
      {/* HERO */}
      <section className="section-padding hero-texture min-h-[70vh] flex items-center">
        <div className="container-width relative z-10 vertical-center">
          <motion.div
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            {/* <p className="label-mono mb-6">Security & Compliance</p> */}

            <h1 className="text-2xl md:text-5xl font-bold tracking-tight leading-[1.1] text-neutral-950 mb-8">
              Enterprise-Grade Security Built for AI-Driven Workflows
            </h1>

            <p className="text-lg md:text-xl leading-relaxed text-neutral-600">
              At Bridge Format AIQ, security is embedded into every layer of our
              platform — from data ingestion to AI processing and output
              delivery.
            </p>
            <Link
              to="/contact-us"
              className="btn-primary gap-2 btn-hover-scale mt-8 md:mt-12"
              data-testid="hero-demo-btn"
            >
              Contact us
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Security FEATURES */}
      <SecurityFeatures />

      {/* Secure Infrastructure */}
      <SecureInfrastructure />
      {/* <section className="section-padding bg-neutral-50">
        <div className="container-width">
          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            {securityFeatures.map((feature, index) => (
              <motion.div
                key={feature.title}
                className="feature-card rounded-[4px] group"
                variants={fadeInUp}
              >
                <div className="flex justify-between mb-6">
                  <feature.icon className="w-6 h-6 text-neutral-950 group-hover:text-primary" />
                  <span className="text-xs font-mono text-neutral-400">
                    0{index + 1}
                  </span>
                </div>

                <h3 className="text-base md:text-lg font-semibold text-neutral-950 mb-3">
                  {feature.title}
                </h3>

                <p className="text-sm md:text-base text-neutral-600 leading-relaxed">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section> */}

      {/* Data Protection */}
      <section className="section-padding">
        <div className="container-width">
          <div className="text-center mb-16">
            {/* <p className="label-mono mb-6">Deployment Options</p> */}

            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight text-neutral-950">
              Data Protection at Every Stage
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3">
            {deploymentOptions.map((option, index) => (
              <div
                key={option.name}
                className={`${
                  index === 1 ? "compare-column-highlight" : "compare-column"
                }`}
              >
                <h3
                  className={`text-base md:text-lg font-semibold mb-3 ${
                    index === 1 ? "text-white" : "text-neutral-950"
                  }`}
                >
                  {option.name}
                </h3>

                <p
                  className={`text-sm md:text-base mb-6 ${
                    index === 1 ? "text-neutral-300" : "text-neutral-600"
                  }`}
                >
                  {option.description}
                </p>

                <ul className="space-y-2">
                  {option.features.map((feature) => (
                    <li
                      key={feature}
                      className={`flex items-center gap-2 text-sm ${index === 1 ? "text-white/80" : "text-neutral-600"}`}
                    >
                      <Check className="w-3 h-3" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* COMPLIANCE */}
      <section className="section-padding bg-secondary text-white">
        <div className="container-width">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              {/* <p className="text-xs font-mono uppercase tracking-wider text-white/80 mb-6">
                Compliance Ready
              </p> */}

              <h2 className="text-3xl text-white md:text-5xl font-semibold mb-6">
                Compliance & Industry Standards
              </h2>

              <p className="text-white/80 text-sm md:text-base mb-8">
                Our platform is designed to align with global security and
                privacy standards.
              </p>

              <ul className="space-y-3">
                {[
                  "SOC 2",
                  "GDPR-ready architecture ",
                  "ISO 27001-aligned practices",
                  "HIPAA-ready",
                ].map((item) => (
                  <li key={item} className="flex gap-3">
                    <Shield className="w-4 h-4 text-white/80" />
                    <span className="text-white/80 text-sm">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="border border-white p-8 rounded-[4px]">
              <h3 className="text-xl font-semibold mb-6">
                Request a Security Review
              </h3>

              <p className="text-white/80 text-sm md:text-base mb-6">
                Our team is available to discuss your requirements.
              </p>

              <Link
                to="/contact-us"
                className="btn-secondary rounded-[4px] transform transition duration-200 hover:scale-105"
              >
                Contact Security Team
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding">
        <div className="container-width text-center max-w-2xl mx-auto">
          <h2 className="text-3xl md:text-5xl font-semibold mb-6">
            Want to Learn More About Our Security?
          </h2>

          <p className="text-neutral-600 text-sm md:text-base mb-8">
            Our team is ready to help.
          </p>

          <Link
            to="/contact-us"
            className="btn-primary transform transition-all duration-200 ease-out hover:scale-105 hover:btn-secondary"
          >
            Contact Security Team
          </Link>
        </div>
      </section>
      <FAQ data={faqData} />
    </div>
  );
};

export default Security;
